import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { ArrowRight, CheckCircle, Users, Shield, Bus, Clock, MapPin, Phone, Mail, Award, TrendingUp, Bell } from "lucide-react";
import { Footer } from "@/components/Footer";

export default function ShuttleInfo() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with Enhanced Gradient */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 text-white py-24 px-4 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-5xl relative z-10">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm px-4 py-2 text-sm font-semibold mb-6 border border-white/20">
              <Shield className="w-4 h-4" />
              Everything You Need to Know
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Summerside Camps at SCH Shuttle: <br />
              <span className="text-blue-200">Safe, Reliable Transportation</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl leading-relaxed">
              Shuttlebee partners with Summerside Camps at SCH to provide a safe, reliable, and family-friendly summer transportation
              service for campers. Here's everything you need to know about how the service works and what to
              expect.
            </p>
          </div>
        </div>
      </section>

      {/* Who Operates the Shuttle */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">Who Operates the Shuttle?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full mb-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="hover-lift border-2 hover:border-blue-200 transition-all duration-300 hover:shadow-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-6 shadow-lg">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Licensed & Vetted Providers</h3>
                <p className="text-gray-600 leading-relaxed">
                  Shuttlebee works with <strong className="text-gray-900">licensed, vetted transportation providers</strong> who
                  specialize in student transportation. All partner providers meet or exceed state requirements for safety,
                  insurance, training, and vehicle compliance.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-2 hover:border-blue-200 transition-all duration-300 hover:shadow-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center mb-6 shadow-lg">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Rigorous Selection Process</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our partnerships are built on a foundation of trust, accountability, and a shared commitment to child safety.
                  Transportation providers are regularly evaluated and must maintain exemplary safety records to remain in our
                  network.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who Does the Shuttle Serve */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="mx-auto max-w-7xl px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-4">Who Does the Shuttle Serve?</h2>
            <div className="w-20 h-1 bg-white/30 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover-lift hover:bg-white/15 transition-all duration-300">
              <Users className="w-12 h-12 mb-4 text-blue-200" />
              <h3 className="text-2xl font-bold mb-3">Grade Levels</h3>
              <p className="text-blue-100">Summerside Campers in <strong className="text-white">PreK through 8th</strong></p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover-lift hover:bg-white/15 transition-all duration-300">
              <MapPin className="w-12 h-12 mb-4 text-blue-200" />
              <h3 className="text-2xl font-bold mb-3">Service Area</h3>
              <p className="text-blue-100"><strong className="text-white">Center City and Northwest Philadelphia</strong></p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover-lift hover:bg-white/15 transition-all duration-300">
              <CheckCircle className="w-12 h-12 mb-4 text-blue-200" />
              <h3 className="text-2xl font-bold mb-3">Special Requests</h3>
              <p className="text-blue-100">Families outside these zones may request service based on enrollment density</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">How Much Does It Cost?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our pricing structure is designed to reflect the service area and route distance, ensuring fair and
              transparent costs for all families.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-orange-600 to-orange-700 text-white rounded-3xl p-10 shadow-2xl hover-lift">
                <MapPin className="w-12 h-12 mb-4" />
                <h3 className="text-4xl font-bold mb-2">$165</h3>
                <p className="text-xl font-medium mb-1">per week per camper, roundtrip</p>
                <p className="text-orange-100">from Center City</p>
                <p className="text-sm text-orange-200 mt-2 opacity-0">placeholder</p>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-orange-600 to-orange-700 text-white rounded-3xl p-10 shadow-2xl hover-lift">
                <MapPin className="w-12 h-12 mb-4" />
                <h3 className="text-4xl font-bold mb-2">$75</h3>
                <p className="text-xl font-medium mb-1">per week per camper, roundtrip</p>
                <p className="text-orange-100">from Northwest Philadelphia</p>
                <p className="text-sm text-orange-200 mt-2">includes Roxborough/Manayunk/Chestnut Hill</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Information */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-4">What Type of Vehicle Will My Student Ride In?</h2>
            <div className="w-20 h-1 bg-white/30 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Shuttlebee uses <strong className="text-white">commercially insured minivans and 10 passenger vans</strong> that meet student
              transportation safety standards.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="bg-blue-700/30 backdrop-blur-sm border-2 border-blue-400/30 hover:border-blue-300/50 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/30 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-blue-100" />
                </div>
                <h3 className="text-xl font-bold mb-4">Age-Appropriate Seating</h3>
                <p className="text-blue-100">Children are seated using age- and size-appropriate seating, including booster and car seats when required by law. Parents are responsible for providing any required booster or car seat for their child.</p>
              </CardContent>
            </Card>

            <Card className="bg-blue-700/30 backdrop-blur-sm border-2 border-blue-400/30 hover:border-blue-300/50 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/30 flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-8 h-8 text-blue-100" />
                </div>
                <h3 className="text-xl font-bold mb-4">Safety Technology</h3>
                <p className="text-blue-100">All vehicles are equipped with GPS tracking and safety monitoring systems that allow us to monitor routes in real-time.</p>
              </CardContent>
            </Card>

            <Card className="bg-blue-700/30 backdrop-blur-sm border-2 border-blue-400/30 hover:border-blue-300/50 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/30 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-blue-100" />
                </div>
                <h3 className="text-xl font-bold mb-4">Daily Inspections</h3>
                <p className="text-blue-100">Every vehicle undergoes daily pre-trip safety inspections to ensure all equipment is in perfect working order.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Driver Information with Step Cards */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">Who Are the Shuttle Drivers?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-orange-600 to-orange-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your shuttle will be driven by a <strong className="text-gray-900">Shuttlebee-approved driver</strong> who has met our comprehensive
              qualification standards. We don't take shortcuts when it comes to the people who transport your children.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                number: "01",
                title: "Background Checks & Training",
                description: "Completed comprehensive background checks and child-transport safety training. Every driver undergoes fingerprinting and criminal history screening.",
                icon: Shield
              },
              {
                number: "02",
                title: "Driving Standards",
                description: "Met rigorous motor-vehicle record and insurance standards. Only drivers with clean driving histories and proper commercial licensing are considered.",
                icon: Award
              },
              {
                number: "03",
                title: "Shuttlebee University",
                description: "Completed onboarding through Shuttlebee University, which includes emergency procedures, business continuity, supervision expectations, and communication protocols.",
                icon: Bus
              },
              {
                number: "04",
                title: "Experience",
                description: "Prior experience serving children and youth programs, plus over ten years of driving experience. Our drivers aren't just qualified—they're seasoned professionals.",
                icon: CheckCircle
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                  <Card className="relative bg-white border-2 hover:border-orange-300 transition-all duration-300 hover-lift">
                    <CardContent className="pt-8 pb-8">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                            {item.number}
                          </div>
                        </div>
                        <div className="flex-1">
                          <Icon className="w-8 h-8 text-orange-600 mb-3" />
                          <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hub Stops Process */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-4">Where Does The Shuttle Stop</h2>
            <div className="w-20 h-1 bg-white/30 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Drop-off and pick-up will occur at up to <strong className="text-white">nine Center City Hubstops and three Northwest Philadelphia locations</strong>.
              These hub locations are strategically selected to be convenient, safe, and easily accessible.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
            {[
              {
                number: "01",
                title: "Address Collection",
                description: "We gather home addresses from all enrolled shuttle students to map optimal coverage areas."
              },
              {
                number: "02",
                title: "Route Optimization",
                description: "Our routing algorithm determines the most efficient hub locations to minimize travel time and maximize convenience."
              },
              {
                number: "03",
                title: "Hub Assignment",
                description: "Families receive final hub assignments and detailed location information before the program begins."
              }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover-lift">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center text-white font-bold text-xl mb-6 shadow-xl">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-orange-500/20 backdrop-blur-sm border-l-4 border-orange-500 p-8 rounded-xl">
              <p className="text-lg">
                <strong className="text-orange-300">Important:</strong> Exact route locations and times are <strong className="text-white">determined and finalized based on the
                home addresses of enrolled shuttle students</strong>. This ensures the most efficient and convenient service for all participating families.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Arrival Time */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">When Should My Child Arrive at the Hub Stop?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-orange-600 to-orange-400 mx-auto rounded-full"></div>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-orange-600 to-orange-400 rounded-3xl blur-lg opacity-25 group-hover:opacity-40 transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-orange-600 to-orange-700 rounded-3xl p-16 text-center text-white shadow-2xl">
                <Clock className="w-20 h-20 mx-auto mb-6 animate-float" />
                <div className="text-8xl font-bold mb-4">5</div>
                <p className="text-3xl font-bold mb-2">Minutes Early</p>
                <p className="text-xl text-orange-100">Arrive <strong className="text-white">5 minutes before</strong> the scheduled departure time</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-12">
            <Card className="border-2 hover:border-orange-300 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Why Punctuality Matters</h3>
                <p className="text-gray-600 leading-relaxed">
                  We recommend arriving 5 minutes before the scheduled departure time so drivers can maintain on-time arrival
                  for all families. When everyone arrives on time, the entire route runs smoothly, and all campers get to Summerside Camps at SCH
                  promptly to start their day.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-300 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Real-Time Updates</h3>
                <p className="text-gray-600 leading-relaxed">
                  Families will receive real-time notifications and route status updates through the <strong className="text-gray-900">Parent
                  Communication Hub app</strong>. You'll always know exactly when to expect the shuttle.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">If I Have Questions or Concerns, Who Should I Contact?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Bell,
                title: "Parent Communication Hub App",
                description: "Use the in-app messaging feature to send questions or concerns directly to our support team.",
                color: "blue"
              },
              {
                icon: Mail,
                title: "Email Support",
                email: "support@getshuttlebee.com",
                description: "For detailed questions and thorough responses.",
                color: "emerald"
              },
              {
                icon: Phone,
                title: "Phone Support",
                description: "A dedicated parent support line will be shared before service begins for urgent matters.",
                color: "orange"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="relative group">
                  <div className={`absolute -inset-1 bg-gradient-to-r from-${item.color}-600 to-${item.color}-400 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300`}></div>
                  <Card className="relative border-2 hover:border-blue-300 transition-all duration-300 hover-lift h-full">
                    <CardContent className="pt-8 pb-8 text-center h-full flex flex-col">
                      <div className={`w-16 h-16 bg-gradient-to-br from-${item.color}-600 to-${item.color}-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                      {item.email && (
                        <p className="text-blue-600 font-semibold mb-3">{item.email}</p>
                      )}
                      <p className="text-gray-600 leading-relaxed flex-1">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>

          <Card className="max-w-4xl mx-auto bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
            <CardContent className="pt-8 pb-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">Response Times</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { time: "1 hour", desc: "during active shuttle hours" },
                  { time: "24 hours", desc: "for non-urgent matters" },
                  { time: "Immediately", desc: "for safety concerns" }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">{item.time}</div>
                    <p className="text-gray-700">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-4xl px-6 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-2xl mb-10 text-blue-100">
            Join hundreds of families who trust Shuttlebee for safe, reliable transportation to Summerside Camps at SCH.
          </p>
          <Link to="/summer-camp-quote">
            <Button size="xl" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300 text-lg px-10 py-6">
              Request Summerside Camps at SCH Transportation
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
